import React, { useEffect, useMemo, useState } from "react";
import {
  colors2,
  Divider,
  DropdownMulti,
  DropdownSingle,
  UI_BODY_SM,
  UI_CAPTION_DF,
} from "oolib";
import { TagClear } from "oolib/dist/v2/components/Tags";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useAutoGenFilterConfigsFromTpl } from "@/components/analysisModules/utils/useAutoGenFilterConfigsFromTpl";
import FilterModule from "@/components/discovery/FilterModule";
import { cleanStaleActiveFilters } from "@/components/discovery/FilterModule/utils/cleanStaleActiveFilters";
import { CustomSelectComp } from "@/components/analysisModules/AnnotationExplorer/comps/CustomSelectComp";
import { getTenant } from "@/TENANT_CONFIGS";
import { isFeatureDisabledOnProd } from "@/utils/isFeatureDisabledOnProd";
import styled from "styled-components";


const StyledPanel = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
`;

const StyledLockedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(245, 245, 245, 0.5);
  z-index: 10;
  cursor: not-allowed;
`;

const StyledScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
`;

/**
 * FilterPanel for AI Chat
 *
 * Shows content type selector + auto-generated filters.
 * Locked (read-only) once the first message is sent.
 */
export const FilterPanel = ({
  isLocked,
  selectedTpls,
  setSelectedTpls,
  activeFilters,
  setActiveFilters,
  aiChatAgent,
  setAiChatAgent,
}) => {
  const allTpls = useGetQueryData("allTpls");
  const { FEATURES_DISABLED_ON_PROD: disabledFeatures = [] } = useGetQueryData("platformConfigs") || {};
  const isProd = import.meta.env.VITE_ENV === "prod";
  const tenant = getTenant();
  // V2 agent option is only available when the feature isn't gated off for this
  // tenant on prod. On non-prod it's always available. Tenant whitelist on prod
  // can enable it for specific tenants only.
  const isReactAgentDisabled = isFeatureDisabledOnProd("aiChatReactAgent", {
    disabledFeatures,
    isProd,
    tenant,
  });

  const agentOptions = useMemo(() => {
    const opts = [{ display: "Dots Ask AI V1", value: "v1_singleMultiIntent" }];
    if (!isReactAgentDisabled) {
      opts.push({ display: "Dots Ask AI V2 (beta)", value: "v2_reactAgent" });
    }
    return opts;
  }, [isReactAgentDisabled]);

  // AI-chat-compatible content types = publishing segment
  const tplOptions = useMemo(
    () => allTpls.filter((tpl) => tpl.general?.segment === "publishing"),
    [allTpls]
  );

  const tplOptionsDropdownData = useMemo(
    () =>
      tplOptions.map((d) => ({
        display: d.general?.content?.title || d.kp_content_type,
        value: d.kp_content_type,
      })),
    [tplOptions]
  );

  const selectedTPLsArrayOptions = useMemo(
    () =>
      selectedTpls.map((d) => ({
        display: d.general?.content?.title || d.kp_content_type,
        value: d.kp_content_type,
      })),
    [selectedTpls]
  );

  // Generate filter configs across doc and annotation scopes, for every available
  // dataset; the unselected ones come back disabled.
  const selectedContentTypes = useMemo(
    () => selectedTpls.map((t) => t.kp_content_type),
    [selectedTpls]
  );
  const autoGenFilters = useAutoGenFilterConfigsFromTpl({
    selectedTpls: tplOptions,
    filterScopes: ["doc", "anno"],
    enableAsyncLoading: true,
    enabledContentTypes: selectedContentTypes,
  });

  const filterConfigs = useMemo(() => {
    if (autoGenFilters?.loading) return [];
    return autoGenFilters?.filterConfigs || autoGenFilters || [];
  }, [autoGenFilters]);

  // Clean up stale activeFilters when filterConfigs change (template switch)
  useEffect(() => {
    if (filterConfigs.length === 0 || activeFilters.length === 0) return;
    const cleaned = cleanStaleActiveFilters({ activeFilters, filterConfig: filterConfigs });
    if (cleaned.length !== activeFilters.length) {
      setActiveFilters(cleaned);
    }
  }, [filterConfigs]); // intentionally omit activeFilters to avoid loops

  const handleTemplateSelection = (k, v) => {
    if (isLocked) return;
    const newSelectedTpls = v.map((d) =>
      tplOptions.find((dd) => dd.kp_content_type === d.value)
    );
    setSelectedTpls(newSelectedTpls);
  };

  const handleTagClearClick = (value) => {
    if (isLocked) return;
    setSelectedTpls((prev) =>
      prev.filter((tpl) => tpl.kp_content_type !== value)
    );
  };

  return (
    <StyledPanel>
      {isLocked && <StyledLockedOverlay />}

      <StyledScrollArea>
        {/* Info banner */}
        <div
          style={{
            padding: "0.6rem 1.2rem",
            background: colors2.grey5,
          }}
        >
          <UI_CAPTION_DF style={{ color: colors2.grey60 }}>
            Filters can only be applied before a conversation is started, so as to maintain consistent context for a single conversation thread.
          </UI_CAPTION_DF>
        </div>

        {/* Agent picker — only show if there's more than one option available. */}
        {agentOptions.length > 1 && (
          <>
            <div style={{ padding: "1rem 1.2rem" }}>
              <UI_BODY_SM style={{ marginBottom: "0.4rem" }}>
                AI Chat Agent
              </UI_BODY_SM>
              <DropdownSingle
                genCustomSelectComp={() => (
                  <CustomSelectComp
                    placeholder="Select Agent"
                    display={
                      agentOptions.find((o) => o.value === aiChatAgent)?.display
                    }
                  />
                )}
                popOutOfOverflowHiddenParent={true}
                options={agentOptions}
                value={agentOptions.find((o) => o.value === aiChatAgent)}
                onChange={(k, v) => {
                  if (isLocked) return;
                  if (setAiChatAgent && v?.value) setAiChatAgent(v.value);
                }}
                disabled={isLocked}
              />
            </div>
            <Divider />
          </>
        )}

        {/* Content Type Selector */}
        <div style={{ padding: "1rem 1.2rem" }}>
          <UI_BODY_SM style={{ marginBottom: "0.4rem" }}>
            Content Types
          </UI_BODY_SM>
          <DropdownMulti
            S
            genCustomSelectComp={() => (
              <CustomSelectComp
                placeholder="Select Content Types"
                display={
                  selectedTPLsArrayOptions.length > 0
                    ? `${selectedTPLsArrayOptions.length} Selected`
                    : undefined
                }
              />
            )}
            popOutOfOverflowHiddenParent={true}
            options={tplOptionsDropdownData}
            value={selectedTPLsArrayOptions}
            onChange={handleTemplateSelection}
            disabled={isLocked}
          />
          {selectedTPLsArrayOptions.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "0.4rem",
                flexWrap: "wrap",
                marginTop: "0.4rem",
              }}
            >
              {selectedTPLsArrayOptions.map((v) => (
                <TagClear
                  key={v.value}
                  grey
                  id={v.value}
                  display={v.display}
                  size="XS"
                  onClick={isLocked ? undefined : () => handleTagClearClick(v.value)}
                />
              ))}
            </div>
          )}
        </div>

        <Divider />

        {/* Filters */}
        {filterConfigs.length > 0 && (
          <FilterModule
            usesAnnoFilters={true}
            key={filterConfigs.map((c) => c.sectionId).join("__")}
            filterConfig={filterConfigs}
            contentTypes={selectedTPLsArrayOptions.map((d) => d.value)}
            activeFilters={activeFilters}
            setActiveFilters={isLocked ? () => {} : setActiveFilters}
            skipLocalFilterSave={true}
          />
        )}

        {filterConfigs.length === 0 && !autoGenFilters?.loading && selectedTpls.length > 0 && (
          <div style={{ padding: "1.2rem", textAlign: "center" }}>
            <UI_BODY_SM style={{ color: colors2.grey40 }}>
              No filters available for selected content types.
            </UI_BODY_SM>
          </div>
        )}
      </StyledScrollArea>
    </StyledPanel>
  );
};
